import React, { createContext, useContext, useState, ReactNode } from 'react'

interface ActionPanelsState {
  notesOpen: boolean
  filesOpen: boolean
  tagsOpen: boolean
  dealsOpen: boolean
}

interface ActionPanelsContextType {
  panels: ActionPanelsState
  openPanel: (panel: keyof ActionPanelsState) => void
  closePanel: (panel: keyof ActionPanelsState) => void
  closeAllPanels: () => void
}

const ActionPanelsContext = createContext<ActionPanelsContextType | undefined>(
  undefined
)

interface ActionPanelsProviderProps {
  children: ReactNode
}

export const ActionPanelsProvider: React.FC<ActionPanelsProviderProps> = ({
  children,
}) => {
  const [panels, setPanels] = useState<ActionPanelsState>({
    notesOpen: false,
    filesOpen: false,
    tagsOpen: false,
    dealsOpen: false,
  })

  const openPanel = (panel: keyof ActionPanelsState) => {
    setPanels((prev) => ({
      notesOpen: false,
      filesOpen: false,
      tagsOpen: false,
      dealsOpen: false,
      [panel]: true,
    }))
  }

  const closePanel = (panel: keyof ActionPanelsState) => {
    setPanels((prev) => ({
      ...prev,
      [panel]: false,
    }))
  }

  const closeAllPanels = () => {
    setPanels({
      notesOpen: false,
      filesOpen: false,
      tagsOpen: false,
      dealsOpen: false,
    })
  }

  const value = {
    panels,
    openPanel,
    closePanel,
    closeAllPanels,
  }

  return (
    <ActionPanelsContext.Provider value={value}>
      {children}
    </ActionPanelsContext.Provider>
  )
}

export const useActionPanels = (): ActionPanelsContextType => {
  const context = useContext(ActionPanelsContext)
  if (context === undefined) {
    throw new Error('useActionPanels must be used within ActionPanelsProvider')
  }
  return context
}
